import React, { type HTMLAttributes, useContext, type FC } from 'react';

import { sendBeaconEvent, getI18nData } from '@lucid/utils';
import { getColor, mqMin } from '@lucid/theme';
import { useTheme } from '@emotion/react';

import Accordion from '../accordion/accordion';
import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Text from '../text/text';
import Link from '../link/link';
import type { ComponentLink } from '../../types/generics';
import type { Lucid, I18n } from '../../types/types';
import LayoutContext from '../context/layout-context';

export interface FAQItems {
  /** The path to be provided to link  with default text "Learn More" */
  learnMoreLink?: string;
  /** List of navigational links to be displayed underneath FAQ item desctiption */
  links?: ComponentLink[];
  /** The main text displayed when accordion is collapsed, typically in question format */
  header: string;
  /** The 'answer' to the FAQ. Longer text that displays when FAQ item is expanded */
  description?: string;
}

export interface FAQSectionProps extends HTMLAttributes<HTMLDivElement> {
  /** Determines whether light text is needed for darker backgrounds */
  hasDarkBackground?: boolean;
  /** The title for the FAQ section */
  sectionTitle?: string;
  /** Heading variant for the section title */
  headingVariant?: Lucid.Theme.Component.Heading['Variant'];
  /** Array of collapsible FAQ items */
  items: FAQItems[];
  /** Adds extra padding to Container in FAQ section*/
  extraPadding?: number;
  /** Background variant for nested Accordion components */
  accordionVariant?: Lucid.Theme.Component.Accordion['Item']['Variant'];
  /** Variant for navigational link displayed under FAQ item */
  linkVariant?: Lucid.Theme.Component.Link['Variant'];
  /** The i18n translation data. */
  i18nData?: I18n.DataEdges;
  /** Updates the background color for the FAQ section */
  backgroundColor?: string;
  /** Allows componeent to receive additional styles */
  className?: string;
}
const FAQSection: FC<FAQSectionProps> = ({
  backgroundColor = 'coolGrey.100',
  hasDarkBackground = false,
  sectionTitle,
  items,
  extraPadding,
  headingVariant = 'headingXL',
  accordionVariant = 'transparent',
  linkVariant,
  i18nData,
  className,
  ...rest
}) => {
  const {
    language: { i18nKey },
  } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, i18nKey);

  const theme = useTheme();
  const updatedBackgroundColor = getColor({
    color: backgroundColor,
    theme,
  });

  return (
    <Box
      as="section"
      css={theme => ({ background: updatedBackgroundColor, padding: `${theme.space.s80}px 0` })}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      {...rest}
    >
      <Container extraPadding={extraPadding}>
        <Heading as="h2" color={hasDarkBackground ? 'white' : 'coolGrey.100'} mb={8} variant={headingVariant}>
          {sectionTitle}
        </Heading>
        <Accordion allowMultipleExpanded allowZeroExpanded>
          {items.map((faqItem, idx) => {
            const { learnMoreLink, links, header, description } = faqItem;
            const allFaqLinks = learnMoreLink
              ? [{ linkUrl: learnMoreLink, linkText: i18n('mktg-ct-consideration-faq-link-text') }, ...(links || [])]
              : links;

            return (
              <Accordion.Item key={`faq-${header}-${idx}`} variant={accordionVariant}>
                <Accordion.Heading variant={accordionVariant}>
                  <Accordion.Button
                    hasArrow
                    css={theme => ({ color: hasDarkBackground ? 'white' : theme.colors.coolGrey[100] })}
                    onClick={() =>
                      sendBeaconEvent({
                        name: 'clickedMarketingPageElement',
                        elementLocation: 'body',
                        elementDetail: 'faq',
                        elementType: 'accordion',
                      })
                    }
                    variant={accordionVariant}
                  >
                    {header}
                  </Accordion.Button>
                </Accordion.Heading>
                <Accordion.Panel>
                  <Text
                    css={theme => [
                      {
                        maxWidth: '100%',
                        flex: '0 1 100%',
                        [mqMin.computer]: {
                          flex: '0 1 58%',
                          maxWidth: '58%',
                        },
                      },
                      hasDarkBackground && { color: theme.colors.white },
                    ]}
                    variant="paragraphS400"
                  >
                    {description}
                  </Text>
                  {!!allFaqLinks?.length && (
                    <ul
                      css={theme => ({
                        listStyleType: 'none',
                        margin: theme.space.s0,
                        lineHeight: theme.lineHeights.navLink,
                      })}
                    >
                      {allFaqLinks.map(({ linkText, linkUrl }) => (
                        <li key={linkUrl}>
                          <Link iconAnimation="navigational" to={linkUrl || ''} underline="hover" variant={linkVariant}>
                            {linkText || i18n('mktg_ct_consideration_faq_link_text')}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Container>
    </Box>
  );
};

export default FAQSection;
